import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import type { UserProfile, UserRole } from '../types/api';
import { useUserProfile } from '../context/UserProfileContext';

type UserListResponse = {
  profiles: UserProfile[];
};

type UpdateRoleResponse = {
  profile: UserProfile;
};

const ROLE_OPTIONS: UserRole[] = ['admin', 'user', 'pending'];
const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Admin',
  user: 'User',
  pending: 'Pending',
};

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string') {
      return message;
    }
  }
  return fallback;
};

const AdminPage = () => {
  const { profile } = useUserProfile();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<Record<string, boolean>>({});

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    const response = await supabase.functions.invoke<UserListResponse>('user-roles', {
      body: { action: 'list' },
    });
    if (response.error) {
      setError(getErrorMessage(response.error, 'Failed to load users'));
      setUsers([]);
      setLoading(false);
      return;
    }
    const profiles = response.data?.profiles ?? [];
    profiles.sort((a, b) => a.email.localeCompare(b.email));
    setUsers(profiles);
    setLoading(false);
  }, []);

  useEffect(() => {
    void fetchUsers();
  }, [fetchUsers]);

  const updateRole = useCallback(async (userId: string, role: UserRole) => {
    setSaving((prev) => ({ ...prev, [userId]: true }));
    const response = await supabase.functions.invoke<UpdateRoleResponse>('user-roles', {
      body: { action: 'updateRole', userId, role },
    });
    setSaving((prev) => ({ ...prev, [userId]: false }));
    if (response.error) {
      setError(getErrorMessage(response.error, 'Failed to update role'));
      return;
    }
    if (!response.data?.profile) {
      return;
    }
    const nextProfile = response.data.profile;
    setUsers((prev) => prev.map((user) => (user.id === userId ? nextProfile : user)));
  }, []);

  const handleRoleSelect = (user: UserProfile, nextRole: UserRole) => {
    if (user.role === nextRole) {
      return;
    }
    const confirmed = window.confirm(`Change ${user.email} to ${ROLE_LABELS[nextRole]}?`);
    if (!confirmed) {
      return;
    }
    void updateRole(user.id, nextRole);
  };

  const adminCount = useMemo(() => users.filter((user) => user.role === 'admin').length, [users]);

  return (
    <div className="page admin-page">
      <header className="admin-header">
        <div>
          <p className="eyebrow">Shanework Admin</p>
          <h1>Manage user access</h1>
          <p className="muted">
            Promote teammates when they are ready, keep pending accounts locked down, and ensure there is always an admin available.
          </p>
        </div>
        <div className="admin-actions">
          <Link className="cta cta-secondary" to="/dashboard">
            <span className="material-symbols-rounded" aria-hidden="true">
              dashboard
            </span>
            Dashboard
          </Link>
          <button
            type="button"
            className="cta cta-primary"
            onClick={() => {
              void fetchUsers();
            }}
            disabled={loading}
          >
            <span className="material-symbols-rounded" aria-hidden="true">
              refresh
            </span>
            Refresh
          </button>
        </div>
      </header>

      <section className="admin-summary">
        <div>
          <p className="summary-label">Admins</p>
          <p className="summary-value">{adminCount}</p>
        </div>
        <div>
          <p className="summary-label">Total users</p>
          <p className="summary-value">{users.length}</p>
        </div>
      </section>

      {error ? <p className="error">{error}</p> : null}
      {loading ? <p className="muted">Loading users…</p> : null}

      {!loading && !users.length ? <p className="muted">No users found.</p> : null}

      {users.length ? (
        <div className="admin-table" role="table" aria-label="User roles">
          <div className="admin-row admin-row-header" role="row">
            <span role="columnheader">Email</span>
            <span role="columnheader">Role</span>
            <span role="columnheader">Created</span>
          </div>
          {users.map((user) => {
            const isCurrentUser = user.id === profile?.id;
            const disabled = saving[user.id] ?? false;
            return (
              <div key={user.id} className="admin-row" role="row">
                <span role="cell">
                  <strong>{user.email}</strong>
                  {isCurrentUser ? <small className="admin-chip">You</small> : null}
                </span>
                <span role="cell">
                  <select
                    value={user.role}
                    onChange={(event) => handleRoleSelect(user, event.target.value as UserRole)}
                    disabled={disabled}
                  >
                    {ROLE_OPTIONS.map((role) => (
                      <option key={role} value={role}>
                        {ROLE_LABELS[role]}
                      </option>
                    ))}
                  </select>
                </span>
                <span role="cell">{new Date(user.created_at).toLocaleDateString()}</span>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default AdminPage;

